import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '../../../routes'
import { usePaginatedEventsQuery } from '../../../queries/useEventOperations'
import { humanize } from 'utils'
import { flat } from 'utils'

export interface PaginatedEventsOptions {
  fetchPolicy?: 'cache-first' | 'network-only' | 'no-cache'
}

export const usePaginatedEvents = (options: PaginatedEventsOptions = {}) => {
  const query = useQuery<{
    page?: number
    appliedFacets?: string[]
    queryString?: string
  }>()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const { search: locationSearch } = useLocation()
  const toggleFacet = (facet: string, action: 'remove' | 'add') => {
    const valueToRemove = action === 'remove' ? facet : null
    const updatedFacets = flat([[appliedFacets], facet], 2).filter(
      (i: string) => i !== valueToRemove
    ) as string[]
    const uniqueFacets = updatedFacets.filter(
      (i, idx) => updatedFacets.indexOf(i) === idx
    )
    query.set({ ...query.search, appliedFacets: uniqueFacets })
  }

  const setQueryString = (queryString: string) =>
    query.set({ ...query.search, queryString, page: 1 })

  const { data, isLoading: loading, error, refetch } = usePaginatedEventsQuery({
    queryString: query.search.queryString ?? '',
    facetFilters: query.search.appliedFacets ?? [],
    page: (query.search.page ?? 1) - 1,
    pageSize: 12,
  })

  useEffect(() => {
    if (loading || currentPage - 1 === data?.search?.events?.searchInfo?.page) {
      return
    }
    console.log(`Use Paginated Events Loading Page ${currentPage}`)
    refetch()
    setCurrentPage((data?.search?.events?.searchInfo?.page ?? 0) + 1)
  }, [data, currentPage, setCurrentPage, refetch, loading])

  const pageCount = data?.search?.events?.searchInfo?.pageCount ?? 0
  const events = data?.search?.events?.nodes
  const success = data?.search?.success
  const facets = JSON.parse(data?.search?.events?.searchInfo?.facets || '{}')
  const queryString = query.search.queryString
  const appliedFacets = query.search.appliedFacets ?? []
  const handlePageClick = ({ historyPush }: { historyPush?: boolean }) => (
    page: number
  ) => {
    query.set({ ...query.search, page }, historyPush)
  }

  const humanizedAppliedFacets = flat([appliedFacets], 2)
    .map((f: string) => f.split(':'))
    .map((f: string[]) => ({
      id: f.join(':'),
      name: humanize(f[0]),
      value: humanize(f[1]),
    }))

  return {
    appliedFacets,
    currentPage,
    pageCount,
    error,
    facets,
    loading,
    events,
    queryString,
    setQueryString,
    search: query.search,
    handlePageClick,
    success,
    toggleFacet,
    locationSearch,
    humanizedAppliedFacets,
  }
}
